import { pdfjs } from 'react-pdf';

import { PageDimensions } from './models';
import { PDF_WASM_URL } from './pdfjsAssets';

// Match the worker the editor's render components configure (DocumentCanvas,
// PageThumbnailRail, etc.) so this metadata pass uses the same pdfjs worker.
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * Read every page's native size (PDF user-space points, scale 1.0) from the
 * PDF at `pdfUrl` (VE-21915). Used to convert the PII engine's normalized
 * bounding polys into the editor's PDF-point geometry without waiting for each
 * page to render. Keyed by 1-based page number.
 *
 * This is the standalone metadata pre-pass flagged in DocumentPage — it loads
 * the document once via pdfjs and reads `getViewport({ scale: 1 })` per page.
 */
export async function fetchPdfPageDimensions(
  pdfUrl: string
): Promise<PageDimensions> {
  const dims: PageDimensions = {};

  const doc = await pdfjs.getDocument({ url: pdfUrl, wasmUrl: PDF_WASM_URL })
    .promise;
  try {
    const numPages: number = doc.numPages;
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await doc.getPage(pageNum);

      const viewport = page.getViewport({ scale: 1 });
      dims[pageNum] = {
        width: viewport.width,

        height: viewport.height,
      };
    }
  } finally {
    await doc.destroy();
  }
  return dims;
}

/**
 * How many pages a PDF actually has, read from the BYTES.
 *
 * Exists because the export burns into a file it resolves for itself while
 * every redaction rect comes from the session built against a different one,
 * and the worker's out-of-range handling is `continue` — so a source with
 * fewer pages drops redactions silently and the export still succeeds, takes
 * the `[REDACTED]` name and publishes.
 *
 * MEASURED, NOT ASSERTED FROM STATE. `session.totalPages` is seeded at 1 and
 * corrected only by react-pdf on render; a guard built on it failed exports of
 * perfectly good documents and was reverted. This reads the catalog of the
 * bytes about to be burned, which is the only number that can be wrong in the
 * way that matters.
 *
 * Returns null when the count cannot be established — a document MuPDF will
 * open and pdf.js will not is rare but possible, and refusing an export over a
 * reader disagreement is the failure mode this is trying to avoid.
 */
export async function readPdfPageCount(
  bytes: ArrayBuffer
): Promise<number | null> {
  try {
    // A COPY: pdf.js takes ownership of the buffer it is handed and detaches
    // it, which would leave the burn-in with zero bytes to work from.
    const doc = await pdfjs.getDocument({
      data: new Uint8Array(bytes.slice(0)),
      wasmUrl: PDF_WASM_URL,
    }).promise;
    try {
      return doc.numPages;
    } finally {
      await doc.destroy();
    }
  } catch (e: unknown) {
    console.warn('Could not read the source PDF page count.', e);
    return null;
  }
}
